import { useEffect, useState } from "react";

import { Icon, TapTarget } from "./ui";

export interface CopyableValueProps {
  /** The actual value the user sees and that gets copied to clipboard. */
  value: string;
  /** Tooltip and aria-label when idle. e.g. "Klikk for å kopiere". */
  hint: string;
  /** Tooltip and aria-label after a successful copy. e.g. "Kopiert!". */
  doneLabel: string;
}

/**
 * Inline copy-to-clipboard button rendered as a hyperlink-styled span.
 * Used for emails, org-numbers, correlation IDs — anything a user is
 * likely to want in their clipboard when contacting support.
 *
 * Avoids `mailto:` deliberately: that opens whichever mail app the OS
 * has registered (often the wrong one for the user) and on mobile
 * usually hijacks into a flow the user did not want. Copy + paste into
 * the user's actual mail client is more predictable.
 *
 * Used from the privacy notice (/personvern), terms of service (/vilkar),
 * and anywhere else we surface a value worth quoting back to us.
 */
export function CopyableValue({ value, hint, doneLabel }: CopyableValueProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard API can fail (insecure context, denied permissions).
      // The value is still visible in the page so the user can copy manually.
    }
  };

  return (
    <TapTarget
      onClick={onCopy}
      aria-label={copied ? doneLabel : hint}
      title={copied ? doneLabel : hint}
      className={`inline-flex items-center gap-1.5 rounded-sm align-baseline font-medium transition-colors ${
        copied
          ? "text-emerald-700"
          : "text-accent-700 underline underline-offset-4 hover:text-accent-800"
      }`}
    >
      <span>{value}</span>
      <Icon name={copied ? "check" : "copy"} size="xs" aria-hidden="true" />
    </TapTarget>
  );
}
